import React, { useState, useEffect } from 'react';
import {
  User,
  Phone,
  GraduationCap,
  Award,
  Briefcase,
  FileText,
  ChevronDown,
  ChevronUp,
  Edit3,
  Users,
  Shield,
  CheckCircle,
  Eye,
  Download,
  X,
  ExternalLink,
} from 'lucide-react';
import { ChangeRequestModal } from './ChangeRequestModal';
import { ChangeRequestsSection } from './ChangeRequestsSection';
import type { StudentProfileFormData } from '@/features/etudiant/components/StudentProfileForm';
import { countries as countriesData } from '@/data/countries';
import {
  type SignatureStatus,
  getInstitutionalDocumentPreviewUrl,
  getInstitutionalDocumentDownloadUrl,
} from '@/services/validationService';
import { fetchSeriesBac, type NomenclatureSerieBac } from '@/services/nomenclatureService';

interface StudentProfileViewProps {
  data: Partial<StudentProfileFormData>;
  campus?: { name: string; city: string } | null;
  documents?: Array<{ type: string; previewUrl: string }>;
  showChangeRequest?: boolean;
  /** Signature status for displaying electronic signature traceability */
  signatureStatus?: SignatureStatus;
}

// Helper to format values for display
const formatValue = (value: unknown, fallback = '—'): string => {
  if (value === undefined || value === null || value === '') return fallback;
  return String(value);
};

// Country phone codes for displaying phone numbers with dial code
const COUNTRY_PHONE_CODES: Record<string, { dialCode: string; flag: string }> = {
  'MA': { dialCode: '+212', flag: '🇲🇦' },
  'DZ': { dialCode: '+213', flag: '🇩🇿' },
  'TN': { dialCode: '+216', flag: '🇹🇳' },
  'FR': { dialCode: '+33', flag: '🇫🇷' },
  'ES': { dialCode: '+34', flag: '🇪🇸' },
  'BE': { dialCode: '+32', flag: '🇧🇪' },
  'CA': { dialCode: '+1', flag: '🇨🇦' },
  'US': { dialCode: '+1', flag: '🇺🇸' },
  'GB': { dialCode: '+44', flag: '🇬🇧' },
  'DE': { dialCode: '+49', flag: '🇩🇪' },
  'IT': { dialCode: '+39', flag: '🇮🇹' },
  'NL': { dialCode: '+31', flag: '🇳🇱' },
  'PT': { dialCode: '+351', flag: '🇵🇹' },
  'SA': { dialCode: '+966', flag: '🇸🇦' },
  'AE': { dialCode: '+971', flag: '🇦🇪' },
  'EG': { dialCode: '+20', flag: '🇪🇬' },
  'SN': { dialCode: '+221', flag: '🇸🇳' },
  'CI': { dialCode: '+225', flag: '🇨🇮' },
  'ML': { dialCode: '+223', flag: '🇲🇱' },
  'MR': { dialCode: '+222', flag: '🇲🇷' },
};

// Format phone number with country code (defaults to Morocco if no country specified)
const formatPhoneWithCountry = (phone?: string, countryCode?: string, fallback = '—'): string => {
  if (!phone) return fallback;

  // If phone already starts with + or 00, it already has the dial code - don't add another
  if (phone.startsWith('+') || phone.startsWith('00')) {
    // Try to find the country flag based on the dial code in the phone
    for (const [, data] of Object.entries(COUNTRY_PHONE_CODES)) {
      if (phone.startsWith(data.dialCode)) {
        return `${data.flag} ${phone}`;
      }
    }
    return phone;
  }

  const effectiveCountryCode = countryCode || 'MA';
  const country = COUNTRY_PHONE_CODES[effectiveCountryCode];
  if (country) {
    return `${country.flag} ${country.dialCode} ${phone}`;
  }
  return phone;
};

// Helper to get country name from code
const getCountryName = (codeOrName?: string): string => {
  if (!codeOrName) return '—';
  // Try to find by code first
  const foundByCode = countriesData.find(c => c.code === codeOrName);
  if (foundByCode) return foundByCode.nameFr;
  // If not found by code, it might already be a name
  const foundByName = countriesData.find(c =>
    c.nameFr.toLowerCase() === codeOrName.toLowerCase()
  );
  if (foundByName) return foundByName.nameFr;
  // Return the original value as-is (it's an unknown country)
  return codeOrName;
};

// Section header component
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
}> = ({ title, icon, expanded, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
  >
    <div className="flex items-center gap-3">
      {icon}
      <span className="font-medium text-gray-900">{title}</span>
    </div>
    {expanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
  </button>
);

// Field display component
const Field: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className = '' }) => (
  <div className={className}>
    <dt className="text-sm text-gray-500 mb-1">{label}</dt>
    <dd className="text-gray-900 font-medium">{value}</dd>
  </div>
);

// Labels for select values
const sexeLabels: Record<string, string> = { M: 'Masculin', F: 'Féminin' };
const ouiNonLabels: Record<string, string> = { O: 'Oui', N: 'Non' };
const situationLabels: Record<string, string> = { N: 'Nouveau', R: 'Redoublant', P: 'Promu', D: 'Diplômé' };
const mentionLabels: Record<string, string> = {
  p: 'Passable',
  ab: 'Assez Bien',
  b: 'Bien',
  tb: 'Très Bien',
  e: 'Excellent',
  ns: 'Non Spécifié',
};
const handicapLabels: Record<string, string> = {
  N: 'Aucun',
  A: 'Auditif',
  M: 'Moteur',
  V: 'Visuel',
  AM: 'Auditif et Moteur',
  AV: 'Auditif et Visuel',
  MV: 'Moteur et Visuel',
  T: 'Tous',
  XX: 'Autre',
};

// Document type labels for display
const documentTypeLabels: Record<string, string> = {
  CIN_FRONT: 'CIN (Recto)',
  CIN_BACK: 'CIN (Verso)',
  PASSPORT: 'Passeport',
  PHOTO: 'Photo d\'identité',
  DIPLOMA: 'Diplôme',
  DIPLOME_BAC_SCAN: 'Diplôme du Baccalauréat',
  TRANSCRIPT: 'Relevé de notes',
  BAC_CERTIFICATE: 'Attestation du Baccalauréat',
  BIRTH_CERTIFICATE: 'Acte de naissance',
  RESIDENCE_CERTIFICATE: 'Certificat de résidence',
  CV: 'CV',
  OTHER: 'Autre document',
};

const getDocumentTypeLabel = (type: string): string => {
  return documentTypeLabels[type] || type;
};

export const StudentProfileView: React.FC<StudentProfileViewProps> = ({
  data,
  campus,
  documents = [],
  showChangeRequest = false,
  signatureStatus,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    coordinates: true,
    emergency: true,
    academic: true,
    diploma: true,
    status: true,
    documents: true,
    signatures: true,
  });

  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const [changeRequestsKey, setChangeRequestsKey] = useState(0);
  const [seriesBac, setSeriesBac] = useState<NomenclatureSerieBac[]>([]);
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; url: string; title: string; downloadUrl?: string }>({
    isOpen: false,
    url: '',
    title: '',
  });

  // Load series bac nomenclature
  useEffect(() => {
    const loadSeriesBac = async () => {
      try {
        const series = await fetchSeriesBac();
        setSeriesBac(series);
      } catch (error) {
        console.error('Error loading series bac:', error);
      }
    };
    loadSeriesBac();
  }, []);

  // Helper to get serie bac name from code
  const getSerieBacName = (code?: string): string => {
    if (!code) return '—';
    const serie = seriesBac.find(s => s.code === code);
    return serie ? serie.labelFr : code;
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="space-y-4">
      {/* Change Request Button - Fixed at top */}
      {showChangeRequest && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Edit3 className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Besoin de modifier une information ?</h3>
                <p className="text-sm text-gray-500">Soumettez une demande qui sera examinée par l'administration</p>
              </div>
            </div>
            <button
              onClick={() => setShowChangeRequestModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              Demander une modification
            </button>
          </div>
        </div>
      )}

      {/* Section 1: Identité */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 1: Identité"
          icon={<User className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.identity}
          onToggle={() => toggleSection('identity')}
        />
        {expandedSections.identity && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Nom" value={formatValue(data.nom)} />
              <Field label="Prénom" value={formatValue(data.prenom)} />
              <Field label="Nom (Arabe)" value={formatValue(data.nomArabe)} />
              <Field label="Prénom (Arabe)" value={formatValue(data.prenomArabe)} />
              <Field label="CNIE" value={formatValue(data.cnie)} />
              <Field label="Passeport" value={formatValue(data.passport)} />
              <Field label="Sexe" value={sexeLabels[data.sexe || ''] || formatValue(data.sexe)} />
              <Field label="Date de naissance" value={formatValue(data.dateNaissance)} />
              <Field label="Pays d'origine" value={getCountryName(data.paysOrigine)} />
              <Field label="Situation de handicap" value={handicapLabels[data.handicap || ''] || formatValue(data.handicap)} />
            </dl>
          </div>
        )}
      </div>

      {/* Section 2: Coordonnées */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 2: Coordonnées"
          icon={<Phone className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.coordinates}
          onToggle={() => toggleSection('coordinates')}
        />
        {expandedSections.coordinates && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Email" value={formatValue(data.email)} />
              <Field label="Téléphone" value={formatPhoneWithCountry(data.telephone, data.phoneCountry)} />
              <Field label="Adresse" value={formatValue(data.adresse)} className="md:col-span-2 lg:col-span-3" />
            </dl>
          </div>
        )}
      </div>

      {/* Section 3: Contact d'Urgence */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 3: Contact d'Urgence"
          icon={<Users className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.emergency}
          onToggle={() => toggleSection('emergency')}
        />
        {expandedSections.emergency && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Nom du contact" value={formatValue(data.urgenceNom)} />
              <Field label="Relation" value={formatValue(data.urgenceRelation)} />
              <Field label="Téléphone" value={formatPhoneWithCountry(data.urgenceNumero, data.urgencePhoneCountry)} />
              <Field label="Adresse" value={formatValue(data.urgenceAdresse)} className="md:col-span-2 lg:col-span-3" />
            </dl>
          </div>
        )}
      </div>

      {/* Section 4: Inscription Académique */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 4: Inscription Académique"
          icon={<GraduationCap className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.academic}
          onToggle={() => toggleSection('academic')}
        />
        {expandedSections.academic && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="N° Massar/CNE" value={formatValue(data.numeroMassarCNE)} />
              <Field label="Diplôme préparé" value={formatValue(data.diplomeInscrit)} />
              <Field label="Filière" value={formatValue(data.filiere)} />
              <Field label="Niveau" value={data.niveauFormation ? `${data.niveauFormation}ème année` : '—'} />
              <Field label="Situation" value={situationLabels[data.situation || ''] || formatValue(data.situation)} />
              <Field label="Première année d'inscription" value={formatValue(data.premiereAnneeInscription)} />
              {campus && (
                <Field label="Campus" value={`${campus.name} - ${campus.city}`} />
              )}
            </dl>
          </div>
        )}
      </div>

      {/* Section 5: Diplôme d'Accès */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 5: Diplôme d'Accès"
          icon={<Award className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.diploma}
          onToggle={() => toggleSection('diploma')}
        />
        {expandedSections.diploma && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Field label="Série du Baccalauréat" value={getSerieBacName(data.serieBaccalaureat)} />
              <Field label="Année d'obtention du Bac" value={formatValue(data.anneeObtentionBac)} />
              <Field label="Diplôme d'accès" value={formatValue(data.diplomeAcces)} />
              <Field label="Spécialité du diplôme" value={formatValue(data.specialiteDiplomeAcces)} />
              <Field label="Mention" value={mentionLabels[data.mentionDiplomeAcces || ''] || formatValue(data.mentionDiplomeAcces)} />
              <Field label="Lieu d'obtention" value={formatValue(data.lieuObtentionDiplomeAcces)} />
            </dl>
          </div>
        )}
      </div>

      {/* Section 6: Statut & Aides */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <SectionHeader
          title="Section 6: Statut & Aides"
          icon={<Briefcase className="w-5 h-5 text-primary-600" />}
          expanded={expandedSections.status}
          onToggle={() => toggleSection('status')}
        />
        {expandedSections.status && (
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Field label="Résident" value={ouiNonLabels[data.resident || ''] || formatValue(data.resident)} />
              <Field label="Fonctionnaire" value={ouiNonLabels[data.fonctionnaire || ''] || formatValue(data.fonctionnaire)} />
              <Field label="Pourcentage de bourse" value={data.pourcentageBourse !== undefined ? `${data.pourcentageBourse}%` : '—'} />
              <Field label="Mobilité" value={ouiNonLabels[data.mobilite || ''] || formatValue(data.mobilite)} />
            </dl>
          </div>
        )}
      </div>

      {/* Section 7: Documents */}
      {documents.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <SectionHeader
            title="Section 7: Documents"
            icon={<FileText className="w-5 h-5 text-primary-600" />}
            expanded={expandedSections.documents}
            onToggle={() => toggleSection('documents')}
          />
          {expandedSections.documents && (
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Vos documents personnels téléchargés lors de votre inscription. Ces documents sont en lecture seule.
              </p>
              <div className="space-y-3">
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-100 rounded-lg">
                        <FileText className="w-5 h-5 text-primary-600" />
                      </div>
                      <span className="font-medium text-gray-900">{getDocumentTypeLabel(doc.type)}</span>
                    </div>
                    {doc.previewUrl && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setPreviewModal({ isOpen: true, url: doc.previewUrl, title: getDocumentTypeLabel(doc.type), downloadUrl: doc.previewUrl })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                        >
                          <Eye className="w-4 h-4" />
                          Consulter
                        </button>
                        <a
                          href={doc.previewUrl}
                          download
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                        >
                          <Download className="w-4 h-4" />
                          Télécharger
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section 8: Signatures Électroniques */}
      {signatureStatus?.reglementInterieurSigned && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <SectionHeader
            title="Documents Signés Électroniquement"
            icon={<Shield className="w-5 h-5 text-primary-600" />}
            expanded={expandedSections.signatures}
            onToggle={() => toggleSection('signatures')}
          />
          {expandedSections.signatures && (
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Ces documents ont été signés électroniquement lors de la soumission de votre profil.
                Les signatures sont horodatées et constituent une preuve d&apos;acceptation.
              </p>
              <div className="space-y-4">
                {/* Règlement Intérieur */}
                <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">Règlement Intérieur</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Signé le{' '}
                      <span className="font-medium text-gray-900">
                        {new Date(signatureStatus.reglementInterieurSignedAt!).toLocaleString('fr-FR', {
                          dateStyle: 'full',
                          timeStyle: 'short',
                        })}
                      </span>
                    </p>
                    {signatureStatus.reglementInterieurVersionId && (
                      <p className="text-xs text-gray-500 mt-1">
                        Version du document: {signatureStatus.reglementInterieurVersionId.slice(0, 8)}...
                      </p>
                    )}
                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        onClick={() => setPreviewModal({
                          isOpen: true,
                          url: getInstitutionalDocumentPreviewUrl('REGLEMENT_INTERIEUR_ETUDIANTS'),
                          title: 'Règlement Intérieur',
                          downloadUrl: getInstitutionalDocumentDownloadUrl('REGLEMENT_INTERIEUR_ETUDIANTS'),
                        })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Eye className="w-4 h-4" />
                        Consulter
                      </button>
                      <a
                        href={getInstitutionalDocumentDownloadUrl('REGLEMENT_INTERIEUR_ETUDIANTS')}
                        download
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <Download className="w-4 h-4" />
                        Télécharger
                      </a>
                    </div>
                  </div>
                </div>

                {/* Consentement Médias */}
                {signatureStatus.consentementMediasSigned ? (
                  <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Consentement Réseaux Sociaux</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {signatureStatus.consentementMediasAccepted ? (
                          <>
                            Accepté le{' '}
                            <span className="font-medium text-gray-900">
                              {new Date(signatureStatus.consentementMediasSignedAt!).toLocaleString('fr-FR', {
                                dateStyle: 'full',
                                timeStyle: 'short',
                              })}
                            </span>
                          </>
                        ) : (
                          'Refusé'
                        )}
                      </p>
                      {signatureStatus.consentementMediasVersionId && (
                        <p className="text-xs text-gray-500 mt-1">
                          Version du document: {signatureStatus.consentementMediasVersionId.slice(0, 8)}...
                        </p>
                      )}
                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button
                          onClick={() => setPreviewModal({
                            isOpen: true,
                            url: getInstitutionalDocumentPreviewUrl('CONSENTEMENT_MEDIAS'),
                            title: 'Consentement Réseaux Sociaux',
                            downloadUrl: getInstitutionalDocumentDownloadUrl('CONSENTEMENT_MEDIAS'),
                          })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                        >
                          <Eye className="w-4 h-4" />
                          Consulter
                        </button>
                        <a
                          href={getInstitutionalDocumentDownloadUrl('CONSENTEMENT_MEDIAS')}
                          download
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                        >
                          <Download className="w-4 h-4" />
                          Télécharger
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-gray-700">Consentement Réseaux Sociaux</h4>
                      <p className="text-sm text-gray-500 mt-1">Non signé (optionnel)</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Change Requests Section */}
      {showChangeRequest && (
        <ChangeRequestsSection
          key={changeRequestsKey}
          onRefresh={() => setChangeRequestsKey(prev => prev + 1)}
        />
      )}

      {/* Change Request Modal */}
      <ChangeRequestModal
        isOpen={showChangeRequestModal}
        onClose={() => setShowChangeRequestModal(false)}
        studentData={data as Record<string, unknown>}
        onSuccess={() => {
          setChangeRequestsKey(prev => prev + 1);
        }}
      />

      {/* Document Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setPreviewModal({ isOpen: false, url: '', title: '' })}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-[95vw] h-[90vh] max-w-6xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <FileText className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{previewModal.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                {previewModal.downloadUrl && (
                  <a
                    href={previewModal.downloadUrl}
                    download
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger
                  </a>
                )}
                <a
                  href={previewModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Nouvel onglet
                </a>
                <button
                  onClick={() => setPreviewModal({ isOpen: false, url: '', title: '' })}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-hidden bg-gray-100">
              <iframe
                src={previewModal.url}
                className="w-full h-full border-0"
                title={previewModal.title}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProfileView;
